import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Collapse, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { ExpandLess, ExpandMore, Psychology } from '@mui/icons-material';
import { Section } from '../../ui/Section';
import { AICheckFinding, AICheckSeverity } from '../../../app/services/api/aiChecks/aiChecksTypes';

const severityColor: Record<AICheckSeverity, 'error' | 'warning' | 'info' | 'default'> = {
  Critical: 'error',
  High: 'error',
  Medium: 'warning',
  Low: 'info',
};

const ENTITY_ROUTE: Record<AICheckFinding['entityType'], (id: number | string) => string> = {
  Recipe: (id) => `/range/recipes/${id}`,
  Menu: (id) => `/range/menus/${id}`,
  Item: () => '/range-planning/item-rationalisation',
};

interface AIChecksPanelProps {
  findings: AICheckFinding[];
  /** Collapsed by default when there are no findings needing attention, expanded otherwise. */
  defaultOpen?: boolean;
  title?: string;
}

/** Shared "AI Recipe Checks" overlay - a compact review panel of cross-catalogue findings
 * (missing allergen/carbon data, dietary conflicts, price anomalies, duplicate/inactive items,
 * unrealistic yields). Reused inside Recipe Builder, Menu Builder, Item Rationalisation and
 * Offer Overview so the same checks show wherever they're relevant, scoped by the findings
 * passed in (see aiChecksEngine for how each screen scopes its query). */
const AIChecksPanel = ({ findings, defaultOpen, title }: AIChecksPanelProps) => {
  const { t } = useTranslation('aiChecks');
  const navigate = useNavigate();
  const [open, setOpen] = useState(defaultOpen ?? findings.length > 0);

  useEffect(() => {
    setOpen(defaultOpen ?? findings.length > 0);
  }, [findings.length, defaultOpen]);

  if (findings.length === 0) return null;

  return (
    <Section>
      <Box sx={{ p: 3 }}>
        <Stack direction="row"
          alignItems="center"
          justifyContent="space-between">
          <Stack direction="row"
            alignItems="center"
            spacing={1}>
            <Psychology color="action" />
            <Typography variant="textSM"
              fontWeight={600}>{title ?? t('title', 'AI Recipe Checks')}</Typography>
            <Chip label={t('findingsCount', '{{count}} finding(s)', { count: findings.length })}
              size="small"
              color={findings.some((f) => f.severity === 'High' || f.severity === 'Critical') ? 'error' : 'warning'} />
          </Stack>
          <IconButton size="small"
            onClick={() => setOpen((o) => !o)}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Stack>
        <Collapse in={open}>
          <Table size="small"
            sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('severity', 'Severity')}</TableCell>
                <TableCell>{t('finding', 'Finding')}</TableCell>
                <TableCell>{t('entity', 'Entity')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {findings.map((finding) => (
                <TableRow key={finding.id}>
                  <TableCell>
                    <Chip label={finding.isFutureCapability ? t('futureCapability', 'Future capability') : finding.severity}
                      color={finding.isFutureCapability ? 'default' : severityColor[finding.severity]}
                      size="small"
                      variant={finding.isFutureCapability ? 'outlined' : 'filled'} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="textSM">{finding.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      component="a"
                      variant="textSM"
                      onClick={() => navigate(ENTITY_ROUTE[finding.entityType](finding.entityId))}
                      sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {finding.entityName}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </Box>
    </Section>
  );
};

export default AIChecksPanel;
